import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface AnchoredPopoverProps {
  /** The element the popover hangs off (its trigger, or the wrapper around it). */
  anchorRef: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
  className?: string;
  /** Which edge of the anchor to line the popover up with. */
  align?: 'left' | 'right';
  role?: string;
  ariaLabel?: string;
  /** Extra styles merged after positioning (background, padding, width…). */
  style?: React.CSSProperties;
}

/**
 * A popover rendered into `<body>` and positioned against its anchor.
 *
 * The broker toolbars sit inside containers that clip (`.bv-dashboard` and the
 * OverflowRow both set `overflow: hidden`), and clipping beats z-index — an
 * in-flow absolute popover is simply invisible, with no console error to explain
 * it. Portalling sidesteps every clipping ancestor; `place` keeps the popover on
 * its anchor through scroll and resize.
 */
export const AnchoredPopover: React.FC<AnchoredPopoverProps> = ({
  anchorRef, children, className, align = 'left', role, ariaLabel, style,
}) => {
  const popRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null);

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const a = anchor.getBoundingClientRect();
    const w = popRef.current?.offsetWidth ?? 0;
    const h = popRef.current?.offsetHeight ?? 0;
    const raw = align === 'right' ? a.right - w : a.left;
    // Keep it on screen when the anchor sits near a window edge.
    const left = Math.max(8, Math.min(raw, window.innerWidth - w - 8));

    // Flip above the anchor when there is not room below. Without this a trigger
    // low on the page -- a card near the end of a carousel, or a pinned footer
    // control -- opens a menu that runs off the bottom of the viewport, and
    // because the popover is `position: fixed` it cannot be scrolled to.
    const roomBelow = window.innerHeight - a.bottom - 8;
    const roomAbove = a.top - 8;
    const flip = h > roomBelow && roomAbove > roomBelow;
    const top = flip ? Math.max(8, a.top - h - 4) : a.bottom + 4;

    setPos({ top, left, maxHeight: Math.max(140, flip ? roomAbove : roomBelow) });
  }, [anchorRef, align]);

  useLayoutEffect(place, [place]);

  useEffect(() => {
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [place]);

  return createPortal(
    <div
      ref={popRef}
      className={className}
      role={role}
      aria-label={ariaLabel}
      style={{
        position: 'fixed',
        zIndex: 3000,
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        // Spread last so a caller's own maxHeight still wins.
        maxHeight: pos?.maxHeight,
        overflowY: 'auto',
        ...style,
      }}
    >
      {children}
    </div>,
    document.body,
  );
};
